import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { patientsApi } from '../services/api';
import './clinic.css';
import {italianDay,loadStudioAppointments} from '../services/studioAgenda';

const aiExamples = [
  'Ho un nuovo paziente e devo preparare la prima visita con BIA.',
  'Analizza l’andamento di un paziente e indicami cosa controllare.',
  'Prepara una bozza di piano alimentare personalizzato.'
];

export function Dashboard() {
  const navigate = useNavigate();
  const commandRef = useRef(null);
  const [command, setCommand] = useState('');
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const [patientData, appointmentData] = await Promise.all([
        patientsApi.getAll(),
        loadStudioAppointments(now)
      ]);
      setPatients(patientData || []);
      setAppointments(appointmentData || []);
      setHasLoaded(true);
    } catch (requestError) {
      setError(requestError.message || 'Non è stato possibile aggiornare la panoramica.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const refresh=()=>{if(!document.hidden)load();}; const timer=setInterval(refresh,60000); window.addEventListener('focus',refresh); return()=>{clearInterval(timer);window.removeEventListener('focus',refresh);}; }, [load]);

  const today = appointments.filter(item => italianDay(item.startsAt) === italianDay(new Date()));

  const startWithSibilla = event => {
    event.preventDefault();
    const initialMessage = command.trim();
    if (!initialMessage) {
      commandRef.current?.focus();
      return;
    }
    navigate('/copilot', { state: { initialMessage, autoSubmit: true } });
  };

  const chooseExample = example => {
    setCommand(example);
    commandRef.current?.focus();
  };

  return <main className="clinic-page dashboard-state-surface dashboard-ai-page" aria-busy={loading}>
    <section className="dashboard-ai-hero" aria-labelledby="dashboardAiTitle">
      <div className="dashboard-ai-intro">
        <span className="dashboard-ai-mark" aria-hidden="true">S</span>
        <h1 id="dashboardAiTitle">Descrivi il lavoro clinico.<br/>Sibilla prepara il percorso.</h1>
        <p>Nuovo paziente, prima visita, BIA, andamento o piano alimentare: scrivi la richiesta come la diresti a una collega.</p>
        <div className="dashboard-ai-safety"><span aria-hidden="true"/><strong>Tu mantieni il controllo</strong><small>Nessun dato viene salvato senza la tua conferma.</small></div>
      </div>
      <form className="dashboard-ai-composer" onSubmit={startWithSibilla}>
        <label htmlFor="dashboardAiCommand">Cosa vuoi preparare oggi?</label>
        <textarea ref={commandRef} id="dashboardAiCommand" value={command} onChange={event => setCommand(event.target.value)} placeholder="Es. Mostrami l’andamento di Flavio Perrone e prepara una nuova proposta alimentare…" rows="4" maxLength="4000"/>
        <div className="dashboard-ai-examples" aria-label="Richieste di esempio">
          {aiExamples.map((example, index) => <button key={example} type="button" onClick={() => chooseExample(example)}><span>{index + 1}</span>{example}</button>)}
        </div>
        <div className="dashboard-ai-submit"><small>Sibilla analizzerà la cartella e proporrà i prossimi passi.</small><button type="submit" disabled={!command.trim()}>Prepara con Sibilla</button></div>
      </form>
    </section>

    {error && <div className="dashboard-state-error" role="alert"><div><strong>Panoramica non aggiornata</strong><span>{error} I dati mostrati potrebbero non essere aggiornati.</span></div><button type="button" onClick={load} disabled={loading}>{loading ? 'Nuovo tentativo…' : 'Riprova'}</button></div>}

    <section className="dashboard-context" aria-label="Situazione dello studio" aria-live="polite">
      {loading && !hasLoaded ? [0, 1, 2, 3].map(index => <div className="dashboard-context-item dashboard-state-skeleton" key={index}><span/><strong/><small/></div>)
        : !hasLoaded ? <>
          <div className="dashboard-context-item"><span>Pazienti attivi</span><strong>—</strong><small>Dati non disponibili</small></div>
          <div className="dashboard-context-item"><span>Oggi</span><strong>—</strong><small>Appuntamenti</small></div>
          <div className="dashboard-context-item"><span>Prossimi 7 giorni</span><strong>—</strong><small>Visite</small></div>
          <div className="dashboard-context-item"><span>Strumenti clinici</span><strong>3</strong><small>BIA · Piani · Sistema TS</small></div>
        </> : <>
          <div className="dashboard-context-item"><span>Pazienti attivi</span><strong>{patients.length}</strong><small>Archivio personale</small></div>
          <div className="dashboard-context-item"><span>Oggi</span><strong>{today.length}</strong><small>Appuntamenti</small></div>
          <div className="dashboard-context-item"><span>Prossimi 7 giorni</span><strong>{appointments.length}</strong><small>Visite programmate</small></div>
          <div className="dashboard-context-item"><span>Strumenti clinici</span><strong>3</strong><small>BIA · Piani · Sistema TS</small></div>
        </>}
    </section>

    <div className="clinic-grid two dashboard-secondary"><section className="clinic-panel"><div className="clinic-panel-head"><h2>Prossimi appuntamenti</h2><Link to="/agenda">Vedi agenda</Link></div>
      {loading && !hasLoaded
        ? <div className="dashboard-state-loading" role="status">Sto caricando i prossimi appuntamenti…</div>
        : !hasLoaded
          ? <div className="clinic-empty">Appuntamenti non disponibili. Usa “Riprova” per aggiornare la panoramica.</div>
          : appointments.length
            ? <div className="timeline-list">{appointments.slice(0, 6).map(item => <div className="timeline-row" key={item.id}><div><strong>{new Date(item.startsAt).toLocaleDateString('it-IT',{timeZone:'Europe/Rome'})}</strong><br/><small>{new Date(item.startsAt).toLocaleTimeString('it-IT', { timeZone:'Europe/Rome', hour: '2-digit', minute: '2-digit' })}</small></div><div><strong>{item.displayName || (item.patient ? `${item.patient.lastName} ${item.patient.firstName}` : 'Appuntamento')}</strong><br/><small>{item.type}</small></div><span className="clinic-pill">{item.status}</span></div>)}</div>
            : <div className="clinic-empty">Nessun appuntamento nei prossimi sette giorni.</div>}
    </section>
      <section className="clinic-panel"><div className="clinic-panel-head"><div><h2>Azioni dirette</h2><p>Per operazioni già definite, senza passare dall’assistente.</p></div></div><div className="clinic-form clinic-grid two"><Link className="clinic-button secondary" to="/patients" state={{openCreate:true}}>Nuovo paziente</Link><Link className="clinic-button secondary" to="/bia">Nuova misura BIA</Link><Link className="clinic-button secondary" to="/diets">Piano alimentare</Link><Link className="clinic-button secondary" to="/sistema-ts">Prestazione TS</Link></div></section></div>
  </main>;
}
